import React from 'react';
import {
  Box, ButtonBase, Divider, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { PreSurrenderGuide } from './data';
import { colors, contentBlockShadow } from '../../../theme';
import ChecklistLessonCard from '../Home/Checklist/ChecklistCards';
import { listLessons } from '../../Lesson';
import { track } from '../analytics';

/** Persisted completion key for a guide — shares adopter_checklist_completion storage. */
export const guideTaskKey = (slug: string) => `psr-${slug}`;

const CARD_IMAGE_BY_SLUG = new Map(listLessons().map((lesson) => [lesson.slug, lesson.cardImage]));

interface GuideRowProps {
  guide: PreSurrenderGuide;
  checked: boolean;
  expanded: boolean;
  onToggleCheck: () => void;
  onToggleExpand: () => void;
  registerRef: (el: HTMLDivElement | null) => void;
}

function GuideRow({
  guide, checked, expanded, onToggleCheck, onToggleExpand, registerRef,
}: GuideRowProps) {
  const handleToggleExpand = () => {
    track('presurrender_guide_toggle', { guide_name: guide.title });
    onToggleExpand();
  };

  return (
    <div ref={registerRef}>
      <Box
        onClick={handleToggleExpand}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: { xs: 1.75, md: '15px' },
          px: { xs: 0.25, md: 0.5 },
          cursor: 'pointer',
        }}
      >
        <ButtonBase
          aria-label={checked ? 'Mark guide unreviewed' : 'Mark guide reviewed'}
          onClick={(e) => {
            e.stopPropagation();
            onToggleCheck();
          }}
          sx={{
            flexShrink: 0,
            width: 22,
            height: 22,
            borderRadius: '5px',
            border: checked ? 'none' : `2px solid ${colors.primary[400]}`,
            bgcolor: checked ? colors.primary.main : '#fff',
            transition: 'all 150ms',
          }}
        >
          {checked && <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />}
        </ButtonBase>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 500,
            // QA: match the adopter checklist's rendered title style exactly.
            fontSize: 16,
            lineHeight: 1.3,
            color: checked ? 'text.secondary' : '#000000DE',
          }}
        >
          {guide.header}
        </Typography>
        {expanded
          ? <ExpandLessIcon sx={{ flexShrink: 0, fontSize: 22, color: 'text.secondary' }} />
          : <ExpandMoreIcon sx={{ flexShrink: 0, fontSize: 22, color: 'text.secondary' }} />}
      </Box>
      {expanded && (
        <Box
          sx={{
            pl: 1,
            pr: { xs: 0.25, md: 0.5 },
            pb: { xs: 2, md: 2.25 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1.75,
          }}
        >
          <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.25 }}>
            {guide.bullets.map((bullet) => (
              <Typography
                key={bullet}
                component="li"
                sx={{
                  fontSize: 13.5,
                  color: 'text.primary',
                  lineHeight: 1.45,
                  '&:not(:last-of-type)': { mb: 0.75 },
                }}
              >
                {bullet}
              </Typography>
            ))}
          </Box>
          <ChecklistLessonCard
            card={{
              kind: 'lesson',
              title: guide.title,
              meta: 'Guide',
              cta: 'Open',
              url: `/hub/lessons-and-guides/${guide.slug}`,
              image: CARD_IMAGE_BY_SLUG.get(guide.slug) || undefined,
              icon: 'menu_book',
            }}
            ageBracket="over6"
            taskId={guideTaskKey(guide.slug)}
          />
        </Box>
      )}
    </div>
  );
}

interface ResourceLibraryProps {
  petName: string;
  orgName: string;
  /** Filtered, ordered guides with {pet} tokens already filled. */
  guides: PreSurrenderGuide[];
  checked: Record<string, boolean>;
  expanded: Record<string, boolean>;
  onToggleCheck: (slug: string) => void;
  onToggleExpand: (slug: string) => void;
  registerGuideRef: (slug: string, el: HTMLDivElement | null) => void;
}

/**
 * "Resources for {pet}" — the reason-filtered guide accordion with per-guide
 * reviewed checkboxes (ticket 2530).
 */
function ResourceLibrary({
  petName, orgName, guides, checked, expanded, onToggleCheck, onToggleExpand, registerGuideRef,
}: ResourceLibraryProps) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '12px',
        boxShadow: contentBlockShadow,
        p: { xs: '20px 16px', md: '26px 30px' },
      }}
    >
      <Typography sx={{
 textAlign: 'center', fontWeight: 700, fontSize: { xs: 18, md: 20 }, lineHeight: 1.25,
}}
      >
        {`Resources for ${petName}`}
      </Typography>
      <Typography
        sx={{
          textAlign: 'center', fontSize: { xs: 13, md: 14 }, color: 'text.secondary', mt: 0.75, lineHeight: 1.5,
        }}
      >
        {`Guides selected for the behaviors you told ${orgName} about. Open any section for a few things to try today.`}
      </Typography>
      <Box sx={{ mt: { xs: 2, md: 2.5 } }}>
        {guides.map((guideItem, i) => (
          <React.Fragment key={guideItem.slug}>
            {i > 0 && <Divider />}
            <GuideRow
              guide={guideItem}
              checked={!!checked[guideTaskKey(guideItem.slug)]}
              expanded={!!expanded[guideTaskKey(guideItem.slug)]}
              onToggleCheck={() => onToggleCheck(guideItem.slug)}
              onToggleExpand={() => onToggleExpand(guideItem.slug)}
              registerRef={(el) => registerGuideRef(guideItem.slug, el)}
            />
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

export default ResourceLibrary;
